// Тема студии: базовая neutral + акцент из конфигурации. Один акцент на всё
// приложение; палитра графита задаётся токенами, а не CSS-переопределениями.
import { defineTheme } from '@astryxdesign/core/theme';
import { neutralTheme } from '@astryxdesign/theme-neutral';

const HEX = /^#[0-9a-fA-F]{6}$/;

function luminance(hex: string): number {
  const c = [1, 3, 5].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/** Белый или почти чёрный текст на акценте — что контрастнее (WCAG) */
export function onAccent(hex: string): string {
  const l = luminance(hex);
  const white = 1.05 / (l + 0.05);
  const black = (l + 0.05) / (luminance('#0B0C0E') + 0.05);
  return white >= black ? '#FFFFFF' : '#0B0C0E';
}
const cache = new Map<string, ReturnType<typeof defineTheme>>();

export function studioTheme(accent: string | undefined) {
  const seed = accent && HEX.test(accent) ? accent : '#4690FF';
  const cached = cache.get(seed);
  if (cached) return cached;
  const theme = defineTheme({
    name: `studio-${seed.slice(1).toLowerCase()}`,
    extends: neutralTheme,
    color: { accent: [seed, seed], neutralStyle: 'cool' },
    typography: {
      scale: { base: 15, ratio: 1.2 },
      body: { family: 'Onest', fallbacks: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
      heading: { family: 'Onest', fallbacks: '-apple-system, BlinkMacSystemFont, sans-serif', weight: 'semibold' },
    },
    radius: { base: 4, multiplier: 1.25 },
    tokens: {
      // Акцент студии — ровно цвет из конфига; текст на нём подбирается по контрасту
      '--color-accent': [seed, seed],
      '--color-on-accent': [onAccent(seed), onAccent(seed)],
      '--color-background-body': ['#F3F5F7', '#0F1114'],
      '--color-background-surface': ['#FFFFFF', '#171A1E'],
      '--color-background-card': ['#FFFFFF', '#171A1E'],
      '--color-background-popover': ['#FFFFFF', '#1F2328'],
      '--color-border': ['#05365919', '#E8EEF614'],
      '--color-border-emphasized': ['#CCD3DB', '#3A4048'],
    },
    adaptations: {
      rules: [
        {
          when: { pointer: 'coarse' },
          value: { tokens: { '--size-element-sm': '36px', '--size-element-md': '44px', '--size-element-lg': '52px' } },
        },
      ],
    },
  });
  cache.set(seed, theme);
  return theme;
}
